import { Signal } from 'rwwa-rx-state-machine'
import { isRaceDetails } from '@mobi/betslip/helpers/typeGuards'
import type {
  BetSlipResponse,
  BetSource,
  EventDetails,
  FobBetReceiptResponse,
  Selection,
} from '@mobi/betslip/types'
import { trackBetLoopCopyBetPlaced } from '@core/Areas/BetLoop/helpers/analytics'
import { trackShareBetLinkPlaced } from '@core/Areas/ShareBet/helpers/analytics'
import { BetErrorType, BetErrorTypeLiteral, ensureBetErrorTypeLiteral } from '@core/Data/betting'
import { defaultData as analyticsDefaultData } from '@core/Services/Analytics/helpers/dataLayer'
import type {
  AnalyticsBetData,
  BetConstruction,
} from '@core/Services/Analytics/helpers/dataLayer/types'
import { trackBettingEvent } from '@core/Services/Analytics/helpers/trackers'
import { trackBetConfirmation } from '@core/Services/Analytics/helpers/trackers/betting/trackBetConfirmation'
import { keys as analyticsKeys } from '@core/Services/Analytics/keys'
import { getFeatures } from '@core/State/FeatureFlags/Store/selectors'
import { store } from '@core/Store'

const defaultBetData = analyticsDefaultData.bet

type BetslipAnalyticsSubscriberParams = {
  confirmAllBetsSuccessful: Signal
  getSelection: GetSelection
  construction: BetConstruction
  betSource?: BetSource
  betSourceShareOrigin?: string
}

export function betslipAnalyticsSubscriber({
  confirmAllBetsSuccessful,
  construction,
  getSelection,
  betSource,
  betSourceShareOrigin,
}: BetslipAnalyticsSubscriberParams): void {
  const confirmSignalData: BetSlipResponse[] = confirmAllBetsSuccessful.data

  confirmSignalData.forEach(bet => {
    const isMultiBet = bet.legs && bet.legs.length > 1

    const step2BetData: AnalyticsBetData = {
      ...defaultBetData,
      construction: construction ?? 'betslip',
      productType: isMultiBet ? 'multi' : 'single',
      errorMessage: !bet.success && bet.error ? mapBetErrorTypeIfNonNullable(bet.error.type) : null,
    }

    trackBettingEvent(analyticsKeys.betAccountReview, { bet: step2BetData })

    // ----------------------------- Bet Step 3 -----------------------------
    if (bet.success) {
      if (isMultiBet) {
        const { ticketNumber, winInvestment, bonusBet } = bet.receipt as FobBetReceiptResponse

        if (betSourceShareOrigin && bet.legs) {
          const raceCode = bet.legs.reduce((prev, curr) => {
            const selectionDetails = getSelection(curr.id)?.[1]
            const currMeetingCode = getMeetingCode(selectionDetails)
            return !prev || prev === currMeetingCode ? currMeetingCode : 'combination'
          }, '')

          if (betSource === 'bet-loop') {
            trackBetLoopCopyBetPlaced({
              amountPaid: winInvestment,
              legs: bet.legs.length,
              hasSpecialOffer: false,
              raceCode: raceCode || undefined,
              origin: betSourceShareOrigin,
            })
          } else if (betSource === 'share-bet') {
            trackShareBetLinkPlaced({
              spend: winInvestment,
              legs: bet.legs.length,
              has_special: false,
              raceCode: raceCode || undefined,
              share_origin: betSourceShareOrigin,
            })
          }
        }

        const step3MultiBetData: AnalyticsBetData = {
          ...defaultBetData,
          specialOffers: null,
          id: ticketNumber,
          construction: construction ?? 'betslip',
          productType: 'multi',
          placementMethod: 'account',
          mixed: {
            fixed: {
              cost: winInvestment,
              numberOfTickets: 1,
              bonusCashUsed: bonusBet > 0,
              bonusCashAmount: bonusBet,
            },
          },
        }

        trackBettingEvent(analyticsKeys.betAccountProcessed, { bet: step3MultiBetData })
      } else {
        const betItem = getSelection(bet.id)
        if (!betItem) return

        const [selection, selectionDetails] = betItem
        const {
          ticketNumber,
          winInvestment = 0,
          placeInvestment = 0,
          specialOffers = [],
          betCost = 0,
          bonusBet,
        } = extractReceipt(bet.receipt)
        const features = getFeatures(store.getState())

        trackBetConfirmation({
          ticketNumber,
          selection,
          selectionDetails,
          specialOffers,
          betConstruction: construction,
          betCost,
          winInvestment,
          placeInvestment,
          features,
          betSource: null,
          bonusCashAmount: bonusBet ?? 0,
        })
      }
    }
  })
}

// =============
// Local Helpers
// =============

function mapBetErrorTypeIfNonNullable(
  betErrorType: Maybe<string | number | BetErrorType>
): Nullable<BetErrorTypeLiteral> {
  if (betErrorType === null || betErrorType === undefined || betErrorType === '') {
    return null
  }
  return ensureBetErrorTypeLiteral(betErrorType)
}

function extractReceipt(receipt: BetSlipResponse['receipt']): {
  ticketNumber: number
  winInvestment: number
  placeInvestment: number
  specialOffers: BetSlipResponse['specialOffers']
  betCost: number
  bonusBet?: number
} {
  if (!receipt) {
    return {
      ticketNumber: -1,
      winInvestment: 0,
      placeInvestment: 0,
      specialOffers: [],
      betCost: 0,
    }
  }

  if ('betCost' in receipt) {
    return {
      ticketNumber: receipt.ticketNumber,
      winInvestment: 0,
      placeInvestment: 0,
      specialOffers: [],
      betCost: receipt.betCost,
      bonusBet: receipt.bonusBet,
    }
  }

  return {
    ticketNumber: receipt.ticketNumber,
    winInvestment: receipt.winInvestment,
    placeInvestment: receipt.placeInvestment,
    specialOffers: receipt.specialOffers ?? [],
    betCost: 0,
    bonusBet: receipt.bonusBet,
  }
}

function getMeetingCode(selectionDetails: EventDetails | null | undefined): string {
  if (!selectionDetails) return 'other'
  if (!isRaceDetails(selectionDetails)) return 'other'
  return selectionDetails.races[0]?.meetingCode || 'other'
}

// =====
// Types
// =====

type GetSelection = (id: string) => [Selection, EventDetails] | null
